import { DesignerPlan } from "@/components/CountertopDesigner";
import { EdgeProfilesStrip, usedEdgeProfiles } from "@/components/EdgeProfilesStrip";
import { money } from "@/lib/format";
import { formatInches } from "@/lib/measure";
import { API } from "@/lib/api";
import { rotatePoints } from "@/components/SlabLayoutBoard";

const has = (n) => Math.abs(Number(n || 0)) > 0.004;

const Field = ({ label, value, strong }) =>
  value ? (
    <div className="mt-1.5 text-[13px] leading-snug text-slate-500">
      {label && <span>{label} </span>}
      <span
        className={strong ? "font-semibold text-slate-900" : "text-slate-700"}
      >
        {value}
      </span>
    </div>
  ) : null;

const InfoCard = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
      {title}
    </div>
    {children}
  </div>
);

const SIDE = (i) => String.fromCharCode(65 + (i % 26));

const edgeRows = (pieces) =>
  (pieces || []).flatMap((p) =>
    (p.edges || [])
      .map((e, i) => {
        const len = Math.round((Number(e.length_in) || 0) * 100) / 100;
        const extras = [
          e.seam ? "seam" : "",
          Number(e.backsplash_height_in) > 0
            ? `${formatInches(e.backsplash_height_in)} backsplash`
            : "",
          Number(e.drop_in) > 0
            ? `${/mit/i.test(e.profile || "") || e.miter ? "mitre" : "waterfall"} ${formatInches(e.drop_in)} deep`
            : e.miter
              ? "mitred"
              : "",
        ].filter(Boolean);
        const profile = e.profile || "Unfinished";
        const listed =
          extras.length > 0 || (profile && profile !== "Unfinished");
        if (!listed || len <= 0) return null;
        return {
          key: `${p.id}-${i}`,
          piece: p.label,
          side: SIDE(i),
          len,
          profile,
          extras: extras.join(" · "),
        };
      })
      .filter(Boolean),
  );

// Collapse repeated edges into one line per profile so the quote isn't a wall of identical rows.
const edgeSummary = (pieces) => {
  const map = new Map();
  const order = [];
  for (const r of edgeRows(pieces)) {
    const key = `${r.profile}|${r.extras}`;
    if (!map.has(key)) {
      map.set(key, { profile: r.profile, extras: r.extras, count: 0, len: 0 });
      order.push(key);
    }
    const g = map.get(key);
    g.count += 1;
    g.len += r.len;
  }
  return order.map((k) => map.get(k));
};

const LineRow = ({ name, desc, amount }) => (
  <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_auto] items-baseline gap-3 border-b border-slate-100 px-4 py-2.5 text-[13px]">
    <span className="font-semibold text-slate-900">{name}</span>
    <span className="text-slate-500">{desc}</span>
    <span className="font-mono-data w-28 text-right tabular-nums text-slate-900">
      {money(amount)}
    </span>
  </div>
);

// Largest leftover off-cut across the job's slabs, so customers see the usable remnant.
const bestRemnant = (quote) => {
  const lay = quote?.layout || {};
  const slabs = lay.slabs || [];
  const pls = lay.placements || {};
  if (!slabs.length || !Object.keys(pls).length) return null;
  const byId = {};
  (quote?.areas || []).forEach((a) =>
    (a.pieces || []).forEach((p) => {
      byId[p.id] = p;
    }),
  );
  let best = null;
  slabs.forEach((s, i) => {
    let maxX = 0;
    let maxY = 0;
    let any = false;
    Object.entries(pls).forEach(([pid, pl]) => {
      if ((pl.slab || 0) !== i) return;
      const p = byId[pid];
      if (!p || !(p.points || []).length) return;
      const g = rotatePoints(p.points, pl.rot || 0);
      maxX = Math.max(maxX, (Number(pl.x) || 0) + g.w);
      maxY = Math.max(maxY, (Number(pl.y) || 0) + g.h);
      any = true;
    });
    if (!any) return;
    const L = Number(s.length_in) || 0;
    const W = Number(s.width_in) || 0;
    const rightA = L - maxX > 6 ? (L - maxX) * W : 0;
    const botA = W - maxY > 6 ? (W - maxY) * L : 0;
    if (rightA <= 0 && botA <= 0) return;
    const r = rightA >= botA ? { w: L - maxX, h: W } : { w: L, h: W - maxY };
    const area = r.w * r.h;
    if (!best || area > best.area) best = { w: r.w, h: r.h, area };
  });
  return best;
};

export const QuotePrintout = ({
  quote,
  totals,
  template,
  logoUrl,
  coverUrl,
  heroUrl,
  edgeStyles = [],
}) => {
  const t = template || {};
  const areas = totals?.areas || [];
  const generated = new Date().toLocaleDateString("en-US");
  const summary = [
    ["Material", totals?.material],
    ["Edge work", totals?.edges],
    ["Cut-outs", totals?.cutouts],
    ["Backsplash", totals?.backsplash],
    ["Install", totals?.install],
    ["Template", totals?.template],
    ["Other charges", totals?.other],
  ].filter(([, v]) => has(v));

  return (
    <div
      className="print-sheet mx-auto w-full max-w-[8.5in] bg-white p-5 text-slate-900 sm:p-10"
      data-testid="quote-printout"
    >
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={t.company_name || "Company"}
              data-testid="printout-logo"
              className="print-fixed print-fixed-logo mb-3 h-14 object-contain"
            />
          )}
          <div
            className="text-lg font-bold tracking-tight"
            data-testid="printout-company"
          >
            {t.company_name || "Your company"}
          </div>
          {t.address && (
            <div className="text-[13px] text-slate-500">{t.address}</div>
          )}
          {(t.phone || t.website) && (
            <div className="text-[13px] text-slate-500">
              {[t.phone, t.website].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Quotation
          </div>
          <div
            className="font-mono-data text-xl font-bold tracking-tight"
            data-testid="printout-quote-number"
          >
            {quote.code ? `#${quote.code}` : "Quote"}
          </div>
          <div className="mt-1 text-[12px] text-slate-500">{generated}</div>
          <div className="font-mono-data mt-2 text-2xl font-bold">
            {money(totals?.total)}
          </div>
        </div>
      </header>

      <div className="mt-3 h-1 w-24 rounded-full bg-slate-900" />

      {heroUrl && (
        <figure
          className="mt-6 overflow-hidden rounded-xl border border-slate-200"
          style={{ breakInside: "avoid" }}
          data-testid="printout-hero"
        >
          <img
            src={heroUrl}
            alt="3D preview of your countertops"
            onError={(e) => {
              e.currentTarget.closest("figure").style.display = "none";
            }}
            className="h-56 w-full object-cover sm:h-72"
          />
          <figcaption className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
            <span className="font-bold uppercase tracking-[0.18em] text-slate-400">
              Your kitchen
            </span>
            <span>3D preview built from your drawing · colors are indicative</span>
          </figcaption>
        </figure>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <InfoCard title="Customer">
          <div className="mt-1.5 text-sm font-bold text-slate-900">
            {quote.customer_company || quote.customer_name || "—"}
          </div>
          <Field
            label="Attn:"
            value={
              quote.contact_name ||
              (quote.customer_company ? quote.customer_name : "")
            }
            strong
          />
          <Field value={quote.customer_phone} />
          <Field value={quote.customer_email} />
          <Field value={quote.customer_address} />
          {quote.trade_account && (
            <div className="mt-1.5 inline-block rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
              Trade account
            </div>
          )}
        </InfoCard>

        <InfoCard title="Job address">
          <div className="mt-1.5 whitespace-pre-wrap text-sm font-bold leading-snug text-slate-900">
            {quote.job_address || quote.customer_address || "—"}
          </div>
          <Field label="Project:" value={quote.project_name} strong />
          <Field
            label="Material:"
            value={
              quote.slab_color
                ? `${quote.slab_color} ${quote.thickness || ""}`.trim()
                : ""
            }
            strong
          />
          {(() => {
            const r = bestRemnant(quote);
            return r ? (
              <Field
                label="Largest offcut:"
                value={`${Math.round(r.w)}" × ${Math.round(r.h)}" · ${(r.area / 144).toFixed(1)} ft²`}
                strong
              />
            ) : null;
          })()}
          <Field label="Sales:" value={t.sales_person} strong />
          {coverUrl && (
            <img
              src={coverUrl}
              alt={quote.slab_color || "Material"}
              data-testid="printout-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="mt-3 h-24 w-full rounded-md border border-slate-200 object-cover"
            />
          )}
        </InfoCard>

        {quote.notes ? (
          <InfoCard title="Notes">
            <div className="mt-1.5 whitespace-pre-wrap text-[13px] leading-snug text-slate-600">
              {quote.notes}
            </div>
          </InfoCard>
        ) : (
          <InfoCard title="Summary">
            {summary.slice(0, 4).map(([k, v]) => (
              <div
                key={k}
                className="mt-1.5 flex justify-between gap-2 text-[13px] text-slate-500"
              >
                <span className="truncate">{k}</span>
                <span className="font-mono-data text-slate-700">
                  {money(v)}
                </span>
              </div>
            ))}
          </InfoCard>
        )}
      </div>

      {t.notice_text && (
        <div
          className="mt-5 rounded-xl border-l-4 border-slate-900 bg-slate-50 px-4 py-3 text-[13px] text-slate-600"
          data-testid="printout-notice"
        >
          {t.notice_text}
        </div>
      )}

      {areas.map((a) => {
        const areaPieces =
          (quote.areas || []).find((x) => x.id === a.id)?.pieces || [];
        const areaColor =
          (quote.areas || []).find((x) => x.id === a.id)?.slab_color || "";
        const distinctColors = new Set(
          (quote.areas || []).map((x) => x.slab_color || quote.slab_color).filter(Boolean),
        );
        const slabPhoto =
          distinctColors.size > 1 && areaColor && quote.id
            ? `${API}/public/quote-cover/${quote.id}?w=320&color=${encodeURIComponent(areaColor)}`
            : "";
        const lines = (a.lines || []).filter((l) => !l.hidden);
        const cuts = areaPieces.flatMap((p) =>
          (p.cutouts || []).map((c) => ({ ...c, piece: p.label })),
        );
        if (!lines.length && !areaPieces.length) return null;
        return (
          <section
            key={a.id}
            className="mt-7 overflow-hidden rounded-xl border border-slate-200"
            data-testid={`printout-area-${a.id}`}
          >
            <div className="flex flex-wrap items-baseline gap-2 border-b border-slate-200 bg-slate-900 px-4 py-2.5 text-white">
              <span className="text-sm font-bold tracking-tight">{a.name}</span>
              <span className="ml-auto text-[11px] text-slate-400">
                {a.address || quote.job_address}
              </span>
            </div>

            {lines.length > 0 && (
              <>
                <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_auto] gap-3 border-b border-slate-200 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  <span>Item</span>
                  <span>Description</span>
                  <span className="w-28 text-right">Amount</span>
                </div>
                {lines.map((l) => (
                  <LineRow
                    key={l.key || l.id}
                    name={l.item}
                    desc={l.description || ""}
                    amount={l.total}
                  />
                ))}
              </>
            )}

            {(edgeRows(areaPieces).length > 0 || usedEdgeProfiles(areaPieces).length > 0) && (
              <div
                className="border-b border-slate-100 px-4 py-2.5"
                data-testid={`printout-edges-${a.id}`}
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Edges &amp; treatments
                </div>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                {t.show_edge_images !== false && (
                  <EdgeProfilesStrip
                    pieces={areaPieces}
                    testId={`printout-edge-drawings-${a.id}`}
                    className="mt-2"
                  />
                )}
                <div className="mt-1.5 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                  {edgeSummary(areaPieces).map((r, i) => (
                    <div
                      key={`${r.profile}-${i}`}
                      className="flex items-baseline gap-2 text-[12px] leading-snug"
                    >
                      {(() => {
                        const st = edgeStyles.find((s) => s.name === r.profile);
                        const code = st?.alias || st?.abbr;
                        return code ? (
                          <span className="font-mono-data rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                            {code}
                          </span>
                        ) : null;
                      })()}
                      <span className="font-semibold text-slate-800">
                        {r.profile}
                      </span>
                      <span className="font-mono-data tabular-nums text-slate-500">
                        {r.count} side{r.count > 1 ? "s" : ""} · {(r.len / 12).toFixed(1)} ft
                      </span>
                      {r.extras && (
                        <span className="text-slate-500">· {r.extras}</span>
                      )}
                    </div>
                  ))}
                </div>
                  </div>
                  {slabPhoto && (
                    <figure
                      className="mt-2 w-28 shrink-0"
                      data-testid={`printout-area-slab-${a.id}`}
                    >
                      <img
                        src={slabPhoto}
                        alt={areaColor}
                        onError={(e) => {
                          e.currentTarget.parentElement.style.display = "none";
                        }}
                        className="h-20 w-full rounded-md border border-slate-200 object-cover"
                      />
                      <figcaption className="mt-1 truncate text-center text-[10px] font-semibold text-slate-600">
                        {areaColor}
                      </figcaption>
                    </figure>
                  )}
                </div>
              </div>
            )}

            {cuts.length > 0 && (
              <div
                className="border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-[12px] leading-relaxed text-slate-500"
                data-testid={`printout-cutouts-${a.id}`}
              >
                {cuts.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 py-0.5">
                    {c.product && quote.id && (
                      <img
                        src={`${API}/public/quote-sink-photo/${quote.id}?w=160&product=${encodeURIComponent(c.product)}`}
                        alt={c.product}
                        data-testid={`printout-sink-photo-${c.id}`}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                        className="h-12 w-16 shrink-0 rounded border border-slate-200 bg-white object-contain"
                      />
                    )}
                    <div>
                    <span className="font-semibold text-slate-700">
                      {c.piece}
                    </span>
                    : {c.kind}
                    {c.sink_type ? ` · ${c.sink_type}` : ""} · {c.w}" x {c.h}"
                    {c.shape && c.shape !== "Rectangle" ? ` · ${c.shape}` : ""}
                    {Number(c.faucet_holes) > 0
                      ? ` · ${c.faucet_holes} faucet hole${
                          Number(c.faucet_holes) > 1 ? "s" : ""
                        }`
                      : ""}
                    {c.product ? ` · ${c.product}` : ""}
                    {c.customer_provided && (
                      <span
                        className="ml-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-700"
                        data-testid={`printout-sink-customer-${c.id}`}
                      >
                        Sink provided by customer
                      </span>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {t.show_piece_lines &&
              (a.pieces || []).map((p) => (
                <LineRow
                  key={p.id}
                  name={p.label}
                  desc={`${p.sqft} ft² · edge ${money(p.edge_cost)}`}
                  amount={p.edge_cost + p.cutout_cost}
                />
              ))}

            {lines.length > 0 && (
              <div className="flex items-baseline justify-end gap-3 bg-slate-50 px-4 py-2.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Area subtotal
                </span>
                <span className="font-mono-data w-28 text-right text-sm font-bold tabular-nums">
                  {money(lines.reduce((s, l) => s + Number(l.total || 0), 0))}
                </span>
              </div>
            )}

            {(a.material_options || []).some((o) => o.priced) && (
              <div
                className="border-t-2 border-amber-300 bg-amber-50/50 px-4 py-3"
                data-testid={`printout-options-${a.id}`}
                style={{ breakInside: "avoid" }}
              >
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                  Other colors to consider
                </div>
                <div className="mt-2.5 space-y-2">
                  {(a.material_options || [])
                    .filter((o) => o.priced)
                    .map((o) => {
                      const same = Math.abs(o.delta) < 0.005;
                      const swatch =
                        o.slab_color && quote.id
                          ? `${API}/public/quote-cover/${quote.id}?w=120&color=${encodeURIComponent(o.slab_color)}`
                          : "";
                      return (
                        <div
                          key={o.id}
                          data-testid={`printout-option-${o.id}`}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                            o.recommended
                              ? "border-emerald-400 bg-emerald-50/60 ring-1 ring-emerald-300"
                              : "border-amber-200 bg-white"
                          }`}
                          style={{ breakInside: "avoid" }}
                        >
                          <div
                            className="relative h-16 w-28 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100"
                            data-testid={`printout-option-thumb-${o.id}`}
                          >
                            <span className="absolute inset-0 flex items-center justify-center text-base font-bold uppercase text-slate-400">
                              {(o.slab_color || "?").slice(0, 2)}
                            </span>
                            {swatch && (
                              <img
                                src={swatch}
                                alt={o.slab_color}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {o.recommended && (
                                <span
                                  data-testid={`printout-option-recommended-${o.id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white shadow-sm"
                                >
                                  ★ We recommend
                                </span>
                              )}
                              <span className="truncate text-[14px] font-bold text-slate-900">
                                {o.slab_color}
                              </span>
                            </div>
                            {o.note && (
                              <div className="mt-0.5 truncate text-[12px] text-slate-500">
                                {o.note}
                              </div>
                            )}
                          </div>
                          <div className="ml-auto shrink-0 text-right">
                            <div className="font-mono-data text-[15px] font-bold tabular-nums text-slate-900">
                              {money(o.total)}
                            </div>
                            <span
                              className={`mt-0.5 inline-block rounded-full border px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                                same
                                  ? "border-slate-300 text-slate-500"
                                  : o.delta < 0
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                    : "border-amber-300 bg-amber-50 text-amber-700"
                              }`}
                            >
                              {same
                                ? "Same price"
                                : `${o.delta < 0 ? "−" : "+"}${money(Math.abs(o.delta))}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {t.show_drawings !== false && quote.show_drawings !== false && areaPieces.length > 0 && (
              <div
                className="border-t border-slate-200 p-3"
                data-testid={`printout-drawing-${a.id}`}
              >
                {quote.id ? (
                  <img
                    src={`${API}/public/quote-diagram/${quote.id}?area=${encodeURIComponent(a.id)}&v=${encodeURIComponent(quote.updated_at || "")}`}
                    alt={`${a.name} drawing`}
                    data-testid={`printout-drawing-img-${a.id}`}
                    className="mx-auto max-h-[420px] w-auto max-w-full"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling?.style.removeProperty("display");
                    }}
                  />
                ) : null}
                <div style={quote.id ? { display: "none" } : undefined}>
                <DesignerPlan
                  pieces={areaPieces}
                  light
                  edgeStyles={edgeStyles}
                  hideCutoutDims={quote.hide_cutout_dims !== false}
                />
                </div>
              </div>
            )}
          </section>
        );
      })}

      <div data-testid="printout-closing" style={{ breakInside: "avoid" }}>
      <section className="mt-8 flex justify-end" data-testid="printout-totals">
        <div className="w-full max-w-sm">
          {summary.map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]"
            >
              <span className="text-slate-500">{k}</span>
              <span className="font-mono-data tabular-nums text-slate-800">
                {money(v)}
              </span>
            </div>
          ))}
          <div className="flex justify-between gap-3 border-b border-slate-200 py-2 text-[13px] font-semibold">
            <span>Subtotal</span>
            <span className="font-mono-data tabular-nums">
              {money(totals?.subtotal)}
            </span>
          </div>
          {has(totals?.trade_discount) && (
            <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]">
              <span className="text-slate-500">
                Trade discount ({totals.trade_pct}%)
              </span>
              <span className="font-mono-data tabular-nums text-slate-800">
                -{money(totals.trade_discount)}
              </span>
            </div>
          )}
          {has(totals?.discount) && (
            <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]">
              <span className="text-slate-500">
                {totals?.discount_pct
                  ? `Discount (${totals.discount_pct}%)`
                  : "Discount"}
              </span>
              <span className="font-mono-data tabular-nums text-slate-800">
                -{money(totals.discount)}
              </span>
            </div>
          )}
          {has(totals?.tax) && (
            <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]">
              <span className="text-slate-500">Tax</span>
              <span className="font-mono-data tabular-nums text-slate-800">
                {money(totals.tax)}
              </span>
            </div>
          )}
          <div className="mt-3 flex items-baseline justify-between gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
              Total
            </span>
            <span
              className="font-mono-data text-2xl font-bold tabular-nums"
              data-testid="printout-total"
            >
              {money(totals?.total)}
            </span>
          </div>
        </div>
      </section>

      {quote?.drawing_approval?.status === "approved" && (
        <div
          data-testid="printout-drawing-approval"
          className="mt-6 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-[12px] text-emerald-900"
        >
          Drawing approved by <strong>{quote.drawing_approval.name}</strong> on{" "}
          {new Date(quote.drawing_approval.at).toLocaleString()}
        </div>
      )}

      {(t.has_signature || t.representative) && (
        <div className="mt-8" data-testid="quote-printout-signature">
          {t.has_signature && (
            <img
              src={`${API}/public/company-signature`}
              alt="Signature"
              className="print-fixed print-fixed-signature h-12 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <div className="mt-1 w-56 border-t border-slate-300 pt-1 text-[12px] text-slate-600">
            {t.representative || t.sales_person || t.company_name}
          </div>
        </div>
      )}
      </div>
      {t.terms_text && (
        <div className="mt-7 whitespace-pre-wrap border-t border-slate-200 pt-4 text-[11px] leading-relaxed text-slate-500">
          {t.terms_text}
        </div>
      )}
      {t.footer_text && (
        <div className="mt-4 text-center text-[11px] text-slate-400">
          {t.footer_text}
        </div>
      )}
    </div>
  );
};

export default QuotePrintout;
